// userViolationsAPI.ts
import { databases, appwriteConfig } from '@/lib/appwrite/config';
import { ID, Query } from 'appwrite';
import { logTrustScoreEvent } from './trustScore';

export interface UserViolation {
  $id?: string;
  userId: string;
  violationStage: 'normal' | 'flagged' | 'banned';
  violationCount: number;
  messageText: string;
  lastViolationType: string;
  flaggedUntil?: string; 

  $createdAt?: string;
  $updatedAt?: string;
}

export interface ViolationUpdate {
  violationType: string;
  messageText: string,
  severity: 'warning' | 'violation';
}

/**
 * Get existing user violation record
 */
export async function getUserViolation(userId: string): Promise<UserViolation | null> {
  try {
    const response = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.userViolationsCollectionId, // You'll need to add this to your config
      [Query.equal('userId', userId)]
    );

    if (response.documents.length > 0) {
      return response.documents[0] as unknown as UserViolation;
    }

    return null;
  } catch (error) {
    console.error('Error fetching user violation:', error);
    throw error;
  }
}

/**
 * Create initial user violation record
 */
export async function createUserViolation(
  userId: string, 
  violationData: ViolationUpdate
): Promise<UserViolation> {
  try {
    const newViolation: Omit<UserViolation, '$id' | '$createdAt' | '$updatedAt'> = {
      userId,
      violationStage: violationData.severity === 'violation' ? 'flagged' : 'normal',
      violationCount: 1,
      messageText: violationData.messageText,
      lastViolationType: violationData.violationType,
      flaggedUntil: violationData.severity === 'violation' 
        ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days from now
        : undefined
    };

    const response = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.userViolationsCollectionId,
      ID.unique(),
      newViolation
    );
      await logTrustScoreEvent(
          userId,
          'rule_violation',
          -100,
         'busiconn rule violation found, please read the terms and conditions.'
        );
    return response as unknown as UserViolation;
  } catch (error) {
    console.error('Error creating user violation:', error);
    throw error;
  }
}

/**
 * Update existing user violation record
 */
export async function updateUserViolation(
  userId: string,
  violationId: string,
  violationData: ViolationUpdate,
  currentViolation: UserViolation
): Promise<UserViolation> {
  try {
    const newCount = currentViolation.violationCount + 1;
    let newStage: 'normal' | 'flagged' | 'banned' = currentViolation.violationStage;
    let flaggedUntil = currentViolation.flaggedUntil;

    // Determine new violation stage
    if (violationData.severity === 'violation') {
      if (newCount >= 3) {
        newStage = 'banned';
        flaggedUntil = undefined; // No need for flagged date if banned
      } else if (newCount >= 2) {
        newStage = 'flagged';
        flaggedUntil = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      }
    }

    const updateData = {
      violationStage: newStage,
      violationCount: newCount,
      lastViolationType: violationData.violationType,
      flaggedUntil: flaggedUntil,
      messageText: violationData.messageText
    };

    const response = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.userViolationsCollectionId,
      violationId,
      updateData
    );
      await logTrustScoreEvent(
          userId,
          'rule_violation',
          -100,
         'busiconn rule violation found, please read the terms and conditions.'
        );    
    return response as unknown as UserViolation;
  } catch (error) {
    console.error('Error updating user violation:', error);
    throw error;
  }
}

/**
 * Main function to record a violation (create or update)
 */
export async function recordUserViolation(
  userId: string,
  violationData: ViolationUpdate
): Promise<{
  violation: UserViolation;
  isNewRecord: boolean;
  previousStage?: 'normal' | 'flagged' | 'banned';
}> {
  try {
    const existingViolation = await getUserViolation(userId);

    if (existingViolation) {
      const previousStage = existingViolation.violationStage;
      const updatedViolation = await updateUserViolation(
        existingViolation.userId,
        existingViolation.$id!,
        violationData,
        existingViolation
      );
      
      return {
        violation: updatedViolation,
        isNewRecord: false,
        previousStage
      };
    } else {
      const newViolation = await createUserViolation(userId, violationData);
      
      return {
        violation: newViolation,
        isNewRecord: true
      };
    }
  } catch (error) {
    console.error('Error recording user violation:', error);
    throw error;
  }
}

/**
 * Check if user is currently banned
 */
export async function isUserBanned(userId: string): Promise<boolean> {
  try {
    const violation = await getUserViolation(userId);
    return violation?.violationStage === 'banned' || false;
  } catch (error) {
    console.error('Error checking if user is banned:', error);
    return false; // Default to false if error occurs
  }
}

/**
 * Check if user is currently flagged and still within flag period
 */
export async function isUserFlagged(userId: string): Promise<boolean> {
  try {
    const violation = await getUserViolation(userId);
    
    if (!violation || violation.violationStage !== 'flagged') {
      return false;
    }

    // Check if flag period has expired
    if (violation.flaggedUntil) {
      const flagExpiry = new Date(violation.flaggedUntil);
      const now = new Date();
      
      if (now > flagExpiry) {
        // Flag period expired, update status to normal
        await databases.updateDocument(
          appwriteConfig.databaseId,
          appwriteConfig.userViolationsCollectionId,
          violation.$id!,
          { 
            violationStage: 'normal',
            flaggedUntil: null 
          }
        );
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Error checking if user is flagged:', error);
    return false;
  }
}

/**
 * Get violation type from block result reason
 */
export function getViolationType(reason: string): string {
  const reasonLower = reason.toLowerCase();
  
  if (reasonLower.includes('phone') || reasonLower.includes('contact information')) {
    return 'phone_number_shared';
  } else if (reasonLower.includes('email')) {
    return 'email_shared';
  } else if (reasonLower.includes('whatsapp')) {
    return 'whatsapp_shared';
  } else if (reasonLower.includes('social media')) {
    return 'social_media_shared';
  } else if (reasonLower.includes('upi') || reasonLower.includes('payment')) {
    return 'payment_info_shared';
  } else if (reasonLower.includes('suspicious')) {
    return 'suspicious_content';
  } else if (reasonLower.includes('fragmented')) {
    return 'fragmented_contact_info';
  } else {
    return 'general_violation';
  }
}